
export const FRAC_BITS = 16;
export const FRAC_ONE = 1 << FRAC_BITS;

export const HEADING_BITS = 12;
export const HEADING_STEPS = 1 << HEADING_BITS;
export const HEADING_MASK = HEADING_STEPS - 1;

/** The original's coordinates are 1/100 m; ours are 1/1000 m. */
export const MM_PER_LEGACY_UNIT = 10;

export const EXACT_OPERAND_MAX = 2 ** 26;

/** An operand outside the range in which TypeScript and Python agree. */
export class FixedPointDomainError extends RangeError {}

function checkOperand(name: string, value: number): void {
  // Raised, never clamped: the Python side raises too, so a caller that leaves
  // the domain fails identically on both sides of the wire instead of producing
  // two different worlds. NaN fails this test as well, which is deliberate.
  if (!(value >= -EXACT_OPERAND_MAX && value <= EXACT_OPERAND_MAX)) {
    throw new FixedPointDomainError(
      `${name}=${value} exceeds +/-${EXACT_OPERAND_MAX}; beyond it this port ` +
        "loses precision and the two simulations diverge",
    );
  }
}

export function legacyToMm(value: number): number {
  return value * MM_PER_LEGACY_UNIT;
}

export function mmToLegacy(value: number): number {
  // Python's `//` floors; mirror that rather than truncating toward zero, so a
  // negative legacy coordinate converts the same way on both sides.
  return Math.floor(value / MM_PER_LEGACY_UNIT);
}

// ------------------------------------------------------------------ trig tables

function buildTables(): { sin: Int32Array; cos: Int32Array } {
  const sin = new Int32Array(HEADING_STEPS);
  const cos = new Int32Array(HEADING_STEPS);
  for (let step = 0; step < HEADING_STEPS; step++) {
    const angle = (2.0 * Math.PI * step) / HEADING_STEPS;
    sin[step] = Math.floor(Math.sin(angle) * FRAC_ONE + 0.5);
    cos[step] = Math.floor(Math.cos(angle) * FRAC_ONE + 0.5);
  }
  return { sin, cos };
}

const TABLES = buildTables();

export const SIN_TABLE: Int32Array = TABLES.sin;
export const COS_TABLE: Int32Array = TABLES.cos;

/** Sine of a 12-bit heading, scaled by FRAC_ONE. */
export function sinFx(heading: number): number {
  return SIN_TABLE[heading & HEADING_MASK];
}

/** Cosine of a 12-bit heading, scaled by FRAC_ONE. */
export function cosFx(heading: number): number {
  return COS_TABLE[heading & HEADING_MASK];
}

export const EXPECTED_TRIG_CHECKSUM = 15778160592296963763n;

export function trigChecksum(): bigint {
  const MASK64 = (1n << 64n) - 1n;
  const PRIME = 1099511628211n;
  let acc = 1469598103934665603n;
  for (const table of [SIN_TABLE, COS_TABLE]) {
    for (const value of table) {
      // `BigInt(value) & 0xffffffffn` reproduces Python's `value & 0xFFFFFFFF` on
      // negatives, where JavaScript's 32-bit `&` would yield a negative number.
      acc = ((acc ^ (BigInt(value) & 0xffffffffn)) * PRIME) & MASK64;
    }
  }
  return acc;
}

// ------------------------------------------------------------------- arithmetic

export function mulFx(value: number, scaleFx: number): number {
  checkOperand("value", value);
  checkOperand("scaleFx", scaleFx);
  const result = Math.trunc((value * scaleFx) / FRAC_ONE);
  return result === 0 ? 0 : result;
}

/** Quantise an angle to a 12-bit heading. Host-side helper, not simulation. */
export function headingFromRadians(radians: number): number {
  // Python's `math.floor` refuses a non-finite angle outright (OverflowError on
  // an infinity, ValueError on a NaN). Unguarded, `Math.floor(Infinity) & 4095`
  // is 0 here -- a plausible-looking heading where the server raises.
  if (!Number.isFinite(radians)) {
    throw new FixedPointDomainError(`radians=${radians} is not finite`);
  }
  const steps = Math.floor((radians * HEADING_STEPS) / (2.0 * Math.PI) + 0.5);
  return steps & HEADING_MASK;
}

/** Shortest signed distance from heading `a` to heading `b`, in steps. */
export function headingDelta(a: number, b: number): number {
  const delta = (b - a) & HEADING_MASK;
  return delta > HEADING_STEPS / 2 ? delta - HEADING_STEPS : delta;
}

/** Squared distance in mm^2. Exact -- no square root, no float. */
export function distanceSqMm(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = ax - bx;
  const dy = ay - by;
  checkOperand("dx", dx);
  checkOperand("dy", dy);
  return dx * dx + dy * dy;
}

/** Range test without a square root. */
export function withinRange(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  radiusMm: number,
): boolean {
  checkOperand("radiusMm", radiusMm);
  return distanceSqMm(ax, ay, bx, by) <= radiusMm * radiusMm;
}

export function isqrt(n: number): number {
  if (n < 0) throw new RangeError(`isqrt of negative ${n}`);
  if (n < 2) return n;
  let root = Math.floor(Math.sqrt(n));
  // Math.sqrt can land one either side of the true root for large n.
  while (root * root > n) root -= 1;
  while ((root + 1) * (root + 1) <= n) root += 1;
  return root;
}

/**
 * Fail loudly at startup if this build's trig tables are not the ones the vectors
 * were generated from.
 */
export function assertTrigTables(): void {
  const actual = trigChecksum();
  if (actual !== EXPECTED_TRIG_CHECKSUM) {
    throw new Error(
      `trig table checksum ${actual} != ${EXPECTED_TRIG_CHECKSUM}; ` +
        "this build would desync from the server",
    );
  }
}
